import styled from "@emotion/styled";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Button, CssBaseline, Divider, FormControl, Tab, Tabs, useTheme, Alert, CircularProgress, MenuItem, Select, useMediaQuery, Card, CardMedia } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
import { Box } from "@mui/system";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import { MenuButton } from "../../components/buttons/MenuButton";
import StartIconButton from "../../components/buttons/StartIconButton";
import InfoGrid from "../../components/InfoGrid";
import LanguageSelect from "../../components/LanguageSelect";
import ResponsiveCircularLoading from "../../components/ResponsiveCircularLoading";
import logoEU from "../../images/EU-nl.png";
import logoEIT from "../../images/logo-EIT.svg";
import logoERDF from "../../images/logo_erdf.png";
import logoUIA from "../../images/UIA_logo.png";
import logoVOXPOP from "../../images/VOXPOP_LOGO.png";
import logo from "../../images/pinbike.gif";
import Statistics from "../Statistics";
import OpenDataMap from "./OpenDataMap";
import OpenDataShops from "./OpenDataShops";
import OpenDataAchievements from "./OpenDataAchievements";
import { OpenDataImpact } from "./OpenDataImpact";
import { OpenDataAirQuality } from "./OpenDataAirQuality";
import { OpenDataGroupAirQuality } from "./OpenDataGroupAirQuality";
import OpenDataRanks from "./OpenDataRanks";
import { OPEN_DATA_GROUP_AIR_QUALITY_ORGANIZATIONS, useGetPublicOrganization, useGetPublicOrganizations, useGetPublicOrganizationSettings, useHasImpactFeatures } from "services/ContentOpenManager";
import logoPinBike from "../../images/splash.png";
import PropTypes from "prop-types";
import LogoCarousel from "components/layout/LogoCarousel";
import { LOGHI_GREENRIDE, LOGHI_SIAMUS } from "constants/loghiPartner";
import AlghoBot, { BOT_TYPES } from "components/chatbot/ChatBot";
import { lastMonth } from "services/helper";
import { REFETCH_OPTIONS } from "services/ContentManager";
import useFacebookPixelPageView from "services/useFacebookPixelPageView";

function TabPanel({ children, value, index, boxSx = {}, ...other }) {
  return (
    <div
      style={{ height: 'calc(100% - 48px)' }}
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, height: '100%', ...boxSx }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export function InfoRedAlertBox({textToShow, sx = {}}){
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <Alert
        variant='outlined'
        severity='info'
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
          margin: '0 auto',
          maxWidth: 600,
          width: 'auto',
          borderColor: theme.palette.error.main,
          color: theme.palette.error.main,
          bgcolor: 'background.paper',
          '& .MuiAlert-icon': {
            color: theme.palette.error.main
          },
          [theme.breakpoints.between('sm', 'md')]: {
            maxWidth: '40%',
          },
          [theme.breakpoints.down('sm')]: {
            maxWidth: '80%',
          },
          ...sx
        })}
      >
        {textToShow}
      </Alert>
    </Box>
  );
}

InfoRedAlertBox.propTypes = {
  textToShow: PropTypes.string
}

const StyledTab = styled((props) => <Tab {...props} />)(
  ({ theme }) => ({
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    // color: theme.palette.secondary.light,
    // '&.Mui-selected': {
    //     color: '#fff',
    // },
  }),
);

const useStyles = makeStyles(theme => ({
  page: {
    padding: theme.spacing(2)
  },
  filterBar: {
    padding: theme.spacing(4),
    paddingBottom: 0
  },
  title: {
    paddingLeft: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
    fontSize: 40
  },
  notificationLogo: {
    width: "5vw",
    color: theme.palette.primary.main,
    fontSize: 60,
  },
  initiativeLogo: {
    maxWidth: "80vw",
    maxHeight: "17vh",
  },
}));

export function calculateDaysDifference(start, end) {
  const timeDiff = end - start;
  return timeDiff / (1000 * 60 * 60 * 24);
}

/**
 * la key indica il nome della variant codificato in md5 e presi i primi 3 caratteri
 * 
 * {baseUrl}/open/all/f57 <- greenride
 * {baseUrl}/open/all/7a3 <- siamus
 * 
 */
const odpVariants = {
  'f57': { loghi: LOGHI_GREENRIDE, decodedVariantKey: 'greenride', botType: BOT_TYPES.VOICE, botTypeKey: 'VOICE' },
  '7a3': { loghi: LOGHI_SIAMUS, decodedVariantKey: 'siamus', botType: BOT_TYPES.CHAT, botTypeKey: 'CHAT' }
}

export default function OpenDataPage() {

  useFacebookPixelPageView();

  // PARAMS

  let { id, variantCode } = useParams();
  let isAll = id === "all";

  // STYLE

  const { t, i18n } = useTranslation();
  const theme = useTheme();
  let classes = useStyles();
  const isWideScreen = useMediaQuery(theme.breakpoints.up('sm'));

  // API QUERY

  let { status: organizationsStatus, organizations } = useGetPublicOrganizations();
  let { status, organization } = useGetPublicOrganization(id, isAll);
  const { settings = [], status: settingsStatus } = useGetPublicOrganizationSettings(id, REFETCH_OPTIONS);

  // PAGE BEHAVIOUR

  const today = dayjs().startOf('day');
  const [value, setValue] = useState('statistics');
  const [isInitiativeOnGoing, setIsInitiativeOnGoing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState({});
  const [isShowMap, setIsShowMap] = useState(true);

  // Il periodo consultabile parte da openDataStartDate, non da startDateUrbanPoints: quello
  // e' il periodo di premialita' e, quando un'iniziativa viene riattivata, viene spostato in
  // avanti — con l'effetto di nascondere le edizioni precedenti (su Bologna l'85% dello
  // storico). Se il setting non e' valorizzato si ricade sul comportamento di prima.
  const openDataStart = settings.find((s) => s.organizationSetting === "openDataStartDate")?.value;

  const settingsStart = (
    settings.find((s) => s.organizationSetting === "startDateUrbanPoints")
    || {
      defaultValue: "2019-01-01",
      organizationSetting: "startDateUrbanPoints",
      value: "2019-01-01",
    }
  ).value;

  let interval = {
    start: openDataStart || settingsStart,
    end: (
      settings.find((s) => s.organizationSetting === "endDateUrbanPoints")
       || {
        defaultValue: lastMonth(),
        organizationSetting: "endDateUrbanPoints",
        value: lastMonth(),
      }
    ).value,
  };

  const getMonthsFromDateInterval = (interval, today) => {
    if (!interval) return [];
    const months = [];
    const start = dayjs(interval.start);
    const end = dayjs(interval.end);
    let current = start.clone();

    // Itera sui mesi all'interno dell'intervallo
    while (current.isBefore(end)) {
      let endOfMonth = current.endOf('month');

      // Se la fine del mese supera la data di fine dell'intervallo, la limita
      if (endOfMonth.isAfter(end)) {
        endOfMonth = end.clone();
      }

      // Esclude il mese attuale e futuri
      if (!endOfMonth.isBefore(today)) {
        break;
      }

      // Aggiunge il mese alla lista con le informazioni necessarie
      months.push({
        text: current.format('MMMM YYYY'),
        value: {
          start: current.format('YYYY-MM-DD'),
          end: endOfMonth.format('YYYY-MM-DD'),
          label: current.format('MMMM YYYY'),
          isAllInitiativePeriod: false
        }
      });

      // Passa al mese successivo
      current = current.add(1, 'month').startOf('month');
    }

    // Aggiunge l'opzione per selezionare l'intero periodo
    months.push({
      text: t('allIniziativePeriod'),
      value: {
        start: start.format('YYYY-MM-DD'),
        // Lecce e Braga hanno liquidato i rimborsi a dicembre, dopo endDateUrbanPoints.
        // Number() perche' id arriva da useParams come stringa: "31" === 31 e' falso.
        end: Number(id) === 31 || Number(id) === 32 ? '2023-12-31' : end.format('YYYY-MM-DD'),
        label: t('allIniziativePeriod'),
        isAllInitiativePeriod: true
      }
    });

    return months;
  };

  const changePeriod = (sp) => {
    setSelectedPeriod(sp);

    const daysDiff = calculateDaysDifference(new Date(sp.start), new Date(sp.end));

    if (isAll) {
      if (daysDiff > 31) {
        setIsShowMap(false);
      } else {
        setIsShowMap(true);
      }
    }
  }

  useEffect(() => {
    let sp;
    if (settingsStatus === 'success') {
      sp = getMonthsFromDateInterval(interval, today)
        ?.find(month => month.value.isAllInitiativePeriod)
        ?.value;
      const isOnGoing = dayjs(sp.end).isAfter(today);
      setIsInitiativeOnGoing(!isAll && isOnGoing);
    } else {
      //  set default date to all section
      sp = isAll ? {
        start: "2019-01-01",
        end: lastMonth(),
        label: t('allIniziativePeriod'),
        isAllInitiativePeriod: true
      } : {
        start: "2023-08-01",
        end: "2023-08-31",
        label: "Aug 2023",
      };
    }
    
    changePeriod(sp);

  }, [settingsStatus]);

  if (status === "loading") return <ResponsiveCircularLoading />;

  const isBicification = String((settings.find(s => s.organizationSetting === 'isBicification') || {}).value) === "true";
  const openDataForm = (settings.find(s => s.organizationSetting === 'openDataUserForm') || {}).value;
  return (
    <Grid container sx={{
      display: 'flex',
      minHeight: '100vh',
      maxWidth: '100vw'
    }}>
      <CssBaseline />
      <main style={{
        flexGrow: 1,
        minHeight: '100%',
      }}>
        <Box sx={{
          maxWidth: theme.breakpoints.values.lg,
          marginLeft: 'auto',
          marginRight: 'auto',
          p: 3,
        }}>
          <header>
            <Grid container sx={{ mb: 3 }}>
              <Grid item xs={12} align={"center"}>
                <Typography variant={'h3'} sx={{
                  padding: theme.spacing(1),
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                  textAlign: "center",
                  textTransform: 'uppercase'
                }}
                >
                  {organization.title || t('initiatives')}
                </Typography>
              </Grid>
              {organization.initiativeLogo ? (
                <Grid item xs={12} align={"center"}>
                  <img className={classes.initiativeLogo} src={organization.initiativeLogo} />
                </Grid>
              ) : (
                <Grid container direction="row" justifyContent="center" alignItems="center">
                  {isAll ? (
                    <img src={logoPinBike} />
                  ) : organization.notificationLogo ?
                    <img className={classes.notificationLogo} src={organization.notificationLogo} />
                    :
                    <AccountBalanceIcon className={classes.notificationLogo} sx={{ fontSize: 60 }} />}
                  <div className={classes.title}>{organization.title}</div>
                </Grid>
              )}
            </Grid>
            {interval.start && (
              <Grid container direction="column" sx={{ mb: 3 }}>
                <Grid item>
                  <Typography variant={'h6'} sx={{ px: 1 }} align="center">
                    {t('iniziativePeriod')}
                  </Typography>
                </Grid>

                <Grid item sx={{
                  fontSize: '1.3em',
                  '& .MuiBox-root': {
                    borderBottom: 'none',
                    m: 0
                  }
                }}>
                  <InfoGrid infos={[
                    {
                      isDate: true,
                      field: 'start',
                      label: t('startDate'),
                      xs: '6',
                      md: 'auto'
                    }, {
                      isDate: !isInitiativeOnGoing,
                      field: 'end',
                      label: t('endDate'),
                      xs: '6',
                      md: 'auto'
                    },
                  ]} data={{
                    start: interval.start,
                    end: isInitiativeOnGoing ? t('onGoing') : interval.end
                  }} />
                </Grid>
              </Grid>
            )}
            <Grid container direction="column" sx={{ mb: 3 }}>
              <Grid item>
                <Typography variant={'h6'} sx={{ px: 1 }} align="center">
                  {t('showedValuesMonth')}
                </Typography>
              </Grid>

              <Grid item textAlign={'center'} sx={{
                fontSize: '1.3em',
                '& .MuiBox-root': {
                  borderBottom: 'none',
                  m: 0
                }
              }}>
                <MenuButton
                  title={selectedPeriod.label}
                  items={getMonthsFromDateInterval(interval, today)}
                  onChange={v => changePeriod(v)}
                />
              </Grid>
            </Grid>
          </header>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2, width: 'calc(100vw - 17px)', boxSizing: 'border-box', overflowX: 'auto' }}>
          <Tabs
            value={value}
            onChange={(event, newValue) => setValue(newValue)}
            variant={isWideScreen ? 'standard' : 'scrollable'}
            scrollButtons="auto"
            centered={isWideScreen}
            sx={{
              '& .MuiTabs-flexContainer': {
                width: 'auto',
              }
            }}
          >
            <StyledTab label={t('map')} value="map" />
            <StyledTab label={t('statistics')} value="statistics" />
            {/* <StyledTab label={t('achievements')} value="achievements" /> */}
            {id == 66 && (<StyledTab label={t('airQualityTitle')} value="airQuality" />)}
            {OPEN_DATA_GROUP_AIR_QUALITY_ORGANIZATIONS.includes(Number(id)) && (<StyledTab label={t('groupAirQuality', "Qualità dell'aria di gruppo")} value="groupAirQuality" />)}
            <StyledTab label={t('ranks')} value="ranks" />
            <StyledTab label={t('shops')} value="shops" />
            {!isAll && <StyledTab label={t('impact')} value="impact" />}
          </Tabs>
        </Box>

        <Box sx={{
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          <TabPanel value={value} index={'map'}>

            {isShowMap ? <OpenDataMap interval={selectedPeriod} organization={organization} />
              : <Typography variant={'h6'} sx={{ px: 1 }} align="center">
                {t('heatmapUnavailable')}
              </Typography>}

          </TabPanel>
          <TabPanel value={value} index={'statistics'}>
            <Statistics disableDateInterval overrideInterval={selectedPeriod} startDateUrbanPoints={interval.start} endDateUrbanPoints={interval.end} isOdp={true} />
          </TabPanel>
          {/* <TabPanel value={value} index={'achievements'}>
						<OpenDataAchievements interval={selectedPeriod} organization={id}></OpenDataAchievements>
					</TabPanel> */}
          <TabPanel value={value} index={'ranks'}>
            <InfoRedAlertBox textToShow={t('notAffectedByTimeIntervalAlert')} />
            <OpenDataRanks interval={selectedPeriod}/>
          </TabPanel>
          <TabPanel value={value} index={'shops'}>
            <OpenDataShops/>
          </TabPanel>
          <TabPanel value={value} index={'impact'}>
            <OpenDataImpact interval={selectedPeriod} startDateUrbanPoints={interval.start} />
          </TabPanel>
          <TabPanel value={value} index={'airQuality'}>
            <OpenDataAirQuality interval={selectedPeriod} />
          </TabPanel>
          <TabPanel value={value} index={'groupAirQuality'}>
            <OpenDataGroupAirQuality />
          </TabPanel>
        </Box>

        <Divider sx={{ py: 1 }} />

        <Grid
          container
          alignItems="center"
          justifyContent="space-around" // Distribuisce equamente gli elementi
          spacing={2} // Spaziatura tra elementi
          sx={{ p: 3 }}
        >
          {/* Sezione "Regulation" */}
          {!isAll && (
            <Grid item xs="auto" textAlign="center">
              <Typography variant="h6">{t('regulation')}</Typography>
              <StartIconButton
                title={t('openPdf')}
                startIcon={<PictureAsPdfIcon />}
                href={organization.regulationLink}
                target="_blank"
              />
            </Grid>
          )}

          {/* Sezione "User Form" */}
          {dayjs(interval.end).diff(dayjs(new Date())) > 0 && (
            <Grid item xs="auto" textAlign="center">
              <Typography variant="h6">{t('userForm')}</Typography>
              <Button
                variant="contained"
                href={openDataForm}
                target="_blank"
                disabled={!openDataForm}
              >
                {t('join')}
              </Button>
            </Grid>
          )}

          {/* Sezione "Iniziative (Select con i link)" */}
          <Grid item xs="auto" textAlign="center">
            <Typography variant="h6">{t('otherInitiatives')}</Typography>
            {organizationsStatus === 'loading' ? (
              <CircularProgress size={24} />
            ) : (
              <FormControl sx={{ minWidth: 200, margin: "16px", textAlign: "left" }}>
                <Select
                  displayEmpty
                  value={id === 'all' ? '' : id}
                  size="small"
                  onChange={(e) => window.open(`/open/${e.target.value}`, '_blank')}
                >
                  <MenuItem disabled value="">
                    {t('selectInitiative')}
                    </MenuItem>
                    <MenuItem key={'all'} value={'all'}>
                      {t('generalOdpPage')}
                    </MenuItem>
                    {organizations
                      ?.slice() 
                      .sort((a, b) => a.title.localeCompare(b.title))
                      .map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.title}
                        </MenuItem>
                      ))}

                  </Select>
              </FormControl>
            )}
          </Grid>

          {/* Sezione "Page Language" */}
          <Grid item xs="auto" textAlign="center">
            <Typography variant="h6">{t('pageLanguage')}</Typography>
            <FormControl sx={{ minWidth: 120, margin: "16px" }} size="small">
              <LanguageSelect
                value={i18n.language}
                onChange={({ target }) => i18n.changeLanguage(target.value)}
                color="black"
              />
            </FormControl>
          </Grid>
        </Grid>
        {
          isAll && variantCode && (
            <>
              <Box sx={{ width: 'calc(100vw - 17px)' }}>
                <LogoCarousel loghi={odpVariants[variantCode]?.loghi || []} />
              </Box>

              <AlghoBot key={odpVariants[variantCode]?.botTypeKey} botType={odpVariants[variantCode]?.botType} />
            </>
          )
        }
        <footer style={{ marginBottom: '0px' }}>
          <Grid container alignItems={'center'} justifyContent={'space-evenly'}
            sx={{ bgcolor: '#32323a57', p: 2 }}>
            {isBicification &&
              <Grid item md={8} sx={{ py: { md: 2, xs: 1 }, px: { md: 3, xs: 1 } }}>
                <Box sx={{ width: '100%', maxWidth: '50em', ml: 3 }}>
                  <img style={{ width: 'calc(50% - 8px)', marginRight: '16px' }} src={logoEIT}
                    alt="EIT Urban Mobility logo" />
                  <img style={{ width: 'calc(50% - 8px)' }} src={logoEU} alt="Co-funded by EU" />
                </Box>
              </Grid>}
            {id == 30 &&
              <>
                <Grid item md={3} sx={{ py: { md: 2, xs: 1 }, px: { md: 3, xs: 1 } }}>
                  <Box sx={{ ml: 3 }}>
                    <img style={{ width: '100%', maxWidth: '20em', verticalAlign: 'middle' }} src={logoERDF}
                      alt="European Regional Development Fund" />
                  </Box>
                </Grid>
                <Grid item md={3} sx={{ py: { md: 2, xs: 1 }, px: { md: 3, xs: 1 } }}>
                  <Box sx={{ ml: 3 }}>
                    <img style={{ width: '100%', maxWidth: '20em', verticalAlign: 'middle' }} src={logoUIA}
                      alt="Urban Innovative Actions" />
                  </Box>
                </Grid>
                <Grid item md={3} sx={{ py: { md: 2, xs: 1 }, px: { md: 3, xs: 1 } }}>
                  <Box sx={{ ml: 3, mr: 3 }}>
                    <img style={{ width: '100%', maxWidth: '20em', maxHeight: '20em', verticalAlign: 'middle' }} src={logoVOXPOP}
                      alt="VoxPop Lisbon Mobility Goes Digital" />
                  </Box>
                </Grid>
              </>}
            <Grid item md={id == 30 ? 3 : 4} sx={{ py: { md: 2, xs: 1 }, px: { md: 3, xs: 1 } }}
              textAlign={isBicification || id == 30 ? 'right' : 'center'}>
              <Box sx={{ mr: 3 }}>
                <img style={{ width: '100%', maxWidth: '20em', verticalAlign: 'middle' }}
                  src={logo} alt="logo" />
              </Box>
            </Grid>
            {id == 30 &&
              <Grid item md={12} sx={{ px: { md: 3, xs: 1 } }}>
                <Box sx={{ ml: 3, mr: 3 }}>
                  <Typography variant={'caption'}>
                    Project co-financed by the European Regional Development Fund through the Urban Innovative Actions (UIA) initiative.
                  </Typography>
                </Box>
              </Grid>}
          </Grid>
        </footer>
      </main>
    </Grid>
  );
}
